import { useMemo, useState } from 'react';
import { Navigate, Link } from 'react-router-dom';

import { TrendChart } from '@/components/TrendChart';
import { StatMetric } from '@/components/StatMetric';
import { DashboardIcon, VisitorIcon } from '@/components/Icons';
import { useSessionState } from '@/context/SessionContext';
import { useHomeDashboard } from '@/hooks/useHomeDashboard';
import { formatCompactNumber, formatInrCurrency, getInitials } from '@/lib/format';
import { tintStyle } from '@/lib/style';

export function DashboardPage() {
  const { isReady: sessionReady, isEnterprise } = useSessionState();
  const { data: homeData, isLoading, isFromApi, error, refresh } = useHomeDashboard();
  const [selectedStaffId, setSelectedStaffId] = useState<string | null>(null);

  const topStaffRoster = useMemo(
    () => [...homeData.staff_performance].sort((left, right) => right.bookings - left.bookings).slice(0, 5),
    [homeData.staff_performance]
  );

  const selectedStaff = useMemo(() => {
    if (selectedStaffId) {
      return topStaffRoster.find((staff) => staff.id === selectedStaffId) ?? topStaffRoster[0] ?? null;
    }

    return topStaffRoster[0] ?? null;
  }, [selectedStaffId, topStaffRoster]);

  const serviceMix = useMemo(() => homeData.service_mix.slice(0, 5), [homeData.service_mix]);
  const todayAppointments = useMemo(() => homeData.today_appointments?.slice(0, 4) ?? [], [homeData.today_appointments]);

  if (!sessionReady || isLoading) {
    return <div className="section">Loading dashboard...</div>;
  }

  if (!isEnterprise) {
    return <Navigate to="/attendance" replace />;
  }

  const overview = homeData.overview;
  const metrics = homeData.metrics;

  return (
    <div className="dashboard-page">
      <section className="dashboard-hero">
        <div className="dashboard-hero-copy">
          <span className="eyebrow">
            <DashboardIcon size={14} />
            <strong>Overview</strong>
            <span>{isFromApi ? 'Live API' : homeData.month_label}</span>
          </span>

          <h1 className="display-title">
            <span>Today at a glance</span>
          </h1>

          <p className="copy dashboard-lede">Bookings, staff, and revenue for the week ahead.</p>

          {error ? (
            <div className="notice error">
              <span>{error}</span>
              <button className="ghost-button" type="button" onClick={refresh}>
                Retry
              </button>
            </div>
          ) : null}

          <div className="button-row dashboard-actions">
            <Link className="primary-button" to="/visitor">
              Register visitor
              <VisitorIcon size={16} />
            </Link>
          </div>
        </div>

        <div className="dashboard-metrics-grid">
          <StatMetric
            label="Clients"
            value={formatCompactNumber(overview?.total_clients ?? homeData.clients?.length ?? 0)}
            hint="Total records"
          />
          <StatMetric
            label="Bookings"
            value={formatCompactNumber(metrics.weekly_bookings)}
            hint="This week"
            highlight
          />
          <StatMetric
            label="Today"
            value={formatCompactNumber(homeData.today_appointments?.length ?? 0)}
            hint="Appointments"
          />
          <StatMetric
            label="Revenue"
            value={formatInrCurrency(metrics.weekly_revenue)}
            hint="This week"
          />
        </div>
      </section>

      <section className="dashboard-stage">
        <div className="dashboard-stack">
          <div className="panel dashboard-sales-card">
            <div className="panel-title-row">
              <div>
                <h2 className="panel-title">Top staff</h2>
              </div>
              <div className="badge soft">{selectedStaff ? `${selectedStaff.bookings} bookings` : 'No data'}</div>
            </div>

            <div className="dashboard-avatar-strip" role="listbox" aria-label="Top staff">
              {topStaffRoster.map((staff, index) => {
                const active = selectedStaff?.id === staff.id;

                return (
                  <button
                    key={staff.id}
                    type="button"
                    role="option"
                    aria-selected={active}
                    className={`dashboard-avatar tinted${active ? ' active' : ''}`}
                    style={tintStyle(staff.color)}
                    title={`${staff.name} · ${staff.bookings} bookings`}
                    onClick={() => setSelectedStaffId(staff.id)}>
                    {index === 0 ? <span className="dashboard-avatar-rank">1</span> : null}
                    {getInitials(staff.name)}
                  </button>
                );
              })}
            </div>

            {selectedStaff ? (
              <div className="dashboard-profile">
                <div className="avatar md tinted" style={tintStyle(selectedStaff.color)}>
                  {getInitials(selectedStaff.name)}
                </div>
                <div>
                  <p className="dashboard-profile-name">{selectedStaff.name}</p>
                  <p className="dashboard-profile-meta">
                    {selectedStaff.bookings} bookings · {formatInrCurrency(selectedStaff.revenue)}
                  </p>
                </div>
                <Link className="ghost-button" to={`/staff/${selectedStaff.id}`}>
                  View
                </Link>
              </div>
            ) : (
              <div className="empty-state">No staff performance data is available right now.</div>
            )}
          </div>

          <div className="panel dashboard-mix-card">
            <div className="panel-title-row">
              <div>
                <h2 className="panel-title">Service mix</h2>
              </div>
              <div className="badge soft">{homeData.month_label}</div>
            </div>

            <div className="service-mix-list">
              {serviceMix.map((service) => (
                <div key={service.name} className="service-mix-row">
                  <div className="service-mix-meta">
                    <span className="service-mix-swatch tinted" style={tintStyle(service.color)} />
                    <strong>{service.name}</strong>
                    <span>{service.count} bookings</span>
                  </div>
                  <div className="service-mix-track">
                    <div
                      className="service-mix-fill tinted"
                      style={{ width: `${Math.max(8, service.share)}%`, ...tintStyle(service.color) }}
                    />
                  </div>
                  <span className="service-mix-share">{service.share}%</span>
                </div>
              ))}
              {serviceMix.length === 0 ? (
                <div className="empty-state">No service mix data for this month.</div>
              ) : null}
            </div>
          </div>
        </div>

        <div className="dashboard-side">
          <div className="chart-card dashboard-money-card">
            <div className="panel-title-row">
              <div>
                <div className="badge">Revenue</div>
                <h2 className="panel-title">Weekly trend</h2>
              </div>
              <div className="chart-legend" aria-hidden="true">
                <span className="chart-legend-item">
                  <i className="chart-legend-dot accent" />
                  Revenue
                </span>
                <span className="chart-legend-item">
                  <i className="chart-legend-dot info" />
                  Bookings
                </span>
              </div>
            </div>

            <div className="money-metrics">
              <div className="money-metric">
                <span className="money-metric-label">Ticket</span>
                <strong className="money-metric-value">{formatInrCurrency(metrics.average_ticket)}</strong>
                <span className="money-metric-hint">Per booking</span>
              </div>
              <div className="money-metric">
                <span className="money-metric-label">Sales</span>
                <strong className="money-metric-value">{formatInrCurrency(metrics.weekly_revenue)}</strong>
                <span className="money-metric-hint">Weekly total</span>
              </div>
              <div className="money-metric">
                <span className="money-metric-label">Retention</span>
                <strong className="money-metric-value">{metrics.retention}%</strong>
                <span className="money-metric-hint">Repeat clients</span>
              </div>
            </div>

            <TrendChart data={homeData.weekly_trend} />
          </div>

          <div className="panel dashboard-today-card">
            <div className="panel-title-row">
              <div>
                <h2 className="panel-title">Today</h2>
              </div>
              <Link className="ghost-button" to="/calendar">
                Open calendar
              </Link>
            </div>

            <div className="list">
              {todayAppointments.map((appointment) => (
                <div className="list-item" key={appointment.id}>
                  <div>
                    <strong>{appointment.client_name}</strong>
                    <span>
                      {appointment.time} · {appointment.staff_name}
                    </span>
                    <p>{appointment.services.join(' · ')}</p>
                  </div>
                  <div className="item-meta">
                    <span className="badge soft">{appointment.status.replace('_', ' ')}</span>
                    <strong>{formatInrCurrency(appointment.total_amount)}</strong>
                  </div>
                </div>
              ))}
              {todayAppointments.length === 0 ? (
                <div className="empty-state">No appointments scheduled for today.</div>
              ) : null}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
